import { asyncDerived, asyncWritable } from '@square/svelte-store';
import { user } from './stores/user';
import { getLocalStorage, setLocalStorage, removeLocalStorage } from './utils/local-storage';
import { region, type Region } from './region';
import { toaster } from './toaster';
import { t } from './i18n';
import { derived, get } from 'svelte/store';
import { createQuery, type CreateQueryOptions } from '@tanstack/svelte-query';
import type {
  GetWishlistProductsQuery,
  GetWishlistProductsQueryVariables
} from '$gql/storefront/operations';
import { shopify } from './shopify';
import { drawer } from './stores/drawer';
import { AUTH_DRAWER_IDS } from './constants';
import { lang } from './lang';
import type { LanguageCode } from '$gql/storefront/schemas';
import { optimalUSCountry, parseCountryAsCode } from './country';

export const WISHLIST_KEYS = {
  us: 'hh_wishlist_us',
  uk: 'hh_wishlist_uk',
  eu: 'hh_wishlist_eu'
} as const;

const getSavedItems = (region: Region) => {
  try {
    const saved = getLocalStorage(WISHLIST_KEYS[region]);

    if (!saved) throw new Error('Not saved wishlist');

    const parsed = JSON.parse(saved);

    if (!Array.isArray(parsed) || parsed.some((id) => typeof id !== 'string')) {
      throw new Error('Invalid wishlist');
    }

    return parsed as string[];
  } catch (error) {
    // no saved wishlist, return empty array
    return [];
  }
};

const isWishlistIdValid = (id: string) => {
  return typeof id === 'string' && id.startsWith('gid://shopify/Product/');
};

const wishlistCounterChange = async (id: string, region: Region, type: 'increase' | 'decrease') => {
  const res = await fetch('/api/counter', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id,
      region,
      type
    })
  });

  return res.text();
};

const createWishlist = () => {
  const store = asyncWritable(
    [user, region],
    async ([$user, $region]) => {
      if (!$user) {
        return getSavedItems($region);
      } else {
        const userItems = $user.wishlist?.value.split('\n').filter(isWishlistIdValid) ?? [];
        const saved = getSavedItems($region);

        // items in localstorage
        if (saved.length) {
          const merged = Array.from(new Set([...userItems, ...saved]));
          removeLocalStorage(WISHLIST_KEYS[$region]);
          store.set(merged); // commit to server

          return merged;
        }

        return userItems;
      }
    },
    async (newList, deps) => {
      if (!deps) return;

      const [$user, $region] = deps;

      if (!$user) {
        setLocalStorage(WISHLIST_KEYS[$region], JSON.stringify(newList));
        return;
      }

      const res = await fetch('/api/wishlist', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          wishlist: newList,
          region: $region
        })
      });

      if (!res.ok) {
        toaster({
          type: 'error',
          title: get(t)('wishlist.toast.error.title'),
          description: get(t)('wishlist.toast.error.description')
        });
      }
    }
  );

  function toggle(id: string) {
    store.update((prev) => {
      if (prev.includes(id)) {
        toaster(
          {
            type: 'success',
            title: get(t)('wishlist.toast.removed')
          },
          { id: 'remove-wishlist' }
        );
        wishlistCounterChange(id, get(region), 'decrease');
        return prev.filter((item) => item !== id);
      } else {
        const isLoggedIn = !!get(user)?.id;

        toaster(
          {
            type: 'success',
            title: get(t)('wishlist.toast.added'),
            action: !isLoggedIn
              ? {
                  label: get(t)('wishlist.toast.added.login'),
                  handler: () => drawer.open(AUTH_DRAWER_IDS.LOGIN)
                }
              : undefined
          },
          { id: 'add-wishlist', duration: !isLoggedIn ? 5000 : undefined }
        );
        wishlistCounterChange(id, get(region), 'increase');
        return [id, ...prev];
      }
    });
  }

  function toggleMultiple(ids: string[]) {
    store.update((prev) => {
      const newProducts = ids.filter((id) => !prev.includes(id));
      const isLoggedIn = !!get(user)?.id;

      toaster(
        {
          type: 'success',
          title: get(t)('wishlist.button.multiple_products.added.count', {
            value: newProducts.length.toString()
          }),
          action: !isLoggedIn
            ? {
                label: get(t)('wishlist.toast.added.login'),
                handler: () => drawer.open(AUTH_DRAWER_IDS.LOGIN)
              }
            : undefined
        },
        { id: 'add-wishlist', duration: !isLoggedIn ? 5000 : undefined }
      );

      newProducts.forEach((id) => {
        wishlistCounterChange(id, get(region), 'increase');
      });

      return [...newProducts, ...prev];
    });
  }
  return {
    ...store,
    toggle,
    toggleMultiple
  };
};

export const wishlist = createWishlist();

/* GraphQL */ `
  query getWishlistProducts($ids: [ID!]!, $lang: LanguageCode, $country: CountryCode)
  @inContext(language: $lang, country: $country) {
    nodes(ids: $ids) {
      __typename
      ... on Product {
        ...ProductCard
        ...QuickAddToCart
      }
    }
  }
`;

export const wishlistQuery = derived(
  [wishlist, shopify, lang, optimalUSCountry],
  ([$wishlist, $shopify, $lang, $optimalUSCountry]) => {
    const getQuery = (
      options: CreateQueryOptions<
        GetWishlistProductsQuery['nodes'],
        GetWishlistProductsQueryVariables
      > = {}
    ) =>
      createQuery<GetWishlistProductsQuery['nodes'], GetWishlistProductsQueryVariables>({
        staleTime: Infinity,
        enabled: $wishlist !== undefined,
        queryKey: ['getWishlistProducts', $lang, ($wishlist ?? []).join(',')],
        queryFn: async () => {
          if (!$wishlist?.length) return [];

          const res = await $shopify.getWishlistProducts({
            ids: ($wishlist ?? []).map((id) => btoa(id)),
            lang: $lang.toUpperCase() as LanguageCode,
            country: parseCountryAsCode($optimalUSCountry)
          });

          return res.nodes || [];
        },
        ...options
      });

    return { getQuery };
  }
);

export const currentWishlists = asyncDerived(
  [wishlist, shopify, lang, optimalUSCountry],
  async ([$wishlist, $shopify, $lang, $optimalUSCountry]) => {
    return await $shopify.getWishlistProducts({
      ids: ($wishlist ?? []).map((id) => btoa(id)),
      lang: $lang.toUpperCase() as LanguageCode,
      country: parseCountryAsCode($optimalUSCountry)
    });
  },
  {
    trackState: true
  }
);
